import { useState, useEffect } from "react";
import axios from "axios";
import { getBackendUrl } from "@/utils/apiBase";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

/**
 * Shared hook: loads the active promotion once and exposes a price helper.
 *
 * Backend contract (GET /api/promotions/active):
 *   { id, title, discount_type, discount_value, start_date, end_date, is_active }
 *   or null / 200 when no promo is active.
 *
 * Optional future-proof fields we still handle:
 *   - code (string) : defaults to `title`
 *   - applicable_forfaits (string[]) : if present, restricts the discount
 *
 * Returns:
 *   { promo, loading, getDiscountedPrice(originalPrice, forfaitId?) }
 *
 * getDiscountedPrice yields:
 *   { price, hasDiscount, originalPrice, discountedPrice, promoCode, promoLabel, title, endDate }
 */
export const useActivePromotion = () => {
  const [promo, setPromo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/promotions/active`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        // Normalize: accept { ... } OR [{...}] OR { promotions: [...] }
        let p = null;
        if (Array.isArray(data)) p = data[0] || null;
        else if (data && Array.isArray(data.promotions)) p = data.promotions[0] || null;
        else if (data && typeof data === "object" && data.discount_value !== undefined) p = data;
        setPromo(p);
      })
      .catch(() => {
        if (!cancelled) setPromo(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const getDiscountedPrice = (originalPrice, forfaitId = null) => {
    const base = Number(originalPrice) || 0;
    if (!promo || !promo.discount_value) {
      return { price: base, originalPrice: base, discountedPrice: base, hasDiscount: false };
    }
    // If promo has an explicit forfait whitelist, honour it
    const whitelist = promo.applicable_forfaits;
    if (Array.isArray(whitelist) && whitelist.length > 0 && !whitelist.includes("all") && forfaitId && !whitelist.includes(forfaitId)) {
      return { price: base, originalPrice: base, discountedPrice: base, hasDiscount: false };
    }
    const isPct = promo.discount_type === "percentage";
    const rawDiscounted = isPct
      ? base * (1 - Number(promo.discount_value) / 100)
      : base - Number(promo.discount_value);
    const discountedPrice = Math.max(0, Math.round(rawDiscounted));
    const promoLabel = isPct ? `-${promo.discount_value}%` : `-${promo.discount_value}€`;
    return {
      price: discountedPrice,
      originalPrice: base,
      discountedPrice,
      hasDiscount: true,
      promoCode: promo.code || promo.title || "PROMO",
      promoLabel,
      title: promo.title || promo.code || "",
      endDate: promo.end_date || promo.valid_until || null,
    };
  };

  return { promo, loading, getDiscountedPrice };
};

export default useActivePromotion;
